import { Loader, Text } from '@mantine/core';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Flexer } from '../app/dashboard/elements/LayoutElements';
import { uuid } from '../CodesThatsGottaGoSomewhereElse';
import { Icon } from '../concepts/resources/Icon';
import { Colors } from './Colors';
import { FadeOut } from './Transitions';

export type SavingIndicatorProps = {
    state: 'idle' | 'saving' | 'success' | 'error',
    savingText?: string,
    successText?: string,
    errorText?: string,
};

const IconFrame = styled.div<{ color: string }>`
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 1.25rem;
    height: 1.25rem;
    background: ${p => p.color};
    color: white;
    border-radius: 1000px;
`;

const Saving = ({ text }: { text?: string }) => <>
    <Loader size="xs" color={Colors.positive} />
    <Text c={Colors.positive}>{text || 'Saving...'}</Text>
</>;

const Success = ({ text }: { text?: string }) => <>
    <IconFrame color={Colors.positive}>
        <Icon size="1" src="deqo://icons/hero/check" />
    </IconFrame>
    <Text c={Colors.positive}>{text || 'Changes saved'}</Text>
</>;

const Error = ({ text }: { text?: string }) => <>
    <IconFrame color={Colors.negative}>
        <Icon size="1" src="deqo://icons/hero/x" />
    </IconFrame>
    <Text c={Colors.negative}>{text || 'Error'}</Text>
</>;

export const SavingIndicator = React.memo(({ state, savingText, successText, errorText }: SavingIndicatorProps) => {
    const key = useMemo(() => uuid(), [state, savingText, successText, errorText]);
    if (state === 'idle') return null;
    return <Flexer inline>
        <FadeOut key={key} after={5000} disabled={state === 'saving'}>
            <Flexer inline flex="row" gap=".5" center minHeight="1.875">
                {state === 'saving' && <Saving text={savingText} />}
                {state === 'success' && <Success text={successText} />}
                {state === 'error' && <Error text={errorText} />}
            </Flexer>
        </FadeOut>
    </Flexer>;
});

type Values = {
    count: number,
    saving: boolean,
    error?: Error,
};

export const useSavingIndicatorState = () => {
    const [values, setValues] = useState<Values>({
        count: 0,
        saving: false,
    });

    const state = useMemo(() => {
        if (values.saving) return 'saving';
        if (values.count > 0) return 'success';
        if (values.error) return 'error';
        return 'idle';
    }, [values]);

    const key = useMemo(() => uuid, [values.count]);

    return [{ key, state }, setValues] as const;
};
